import React, { useState, useEffect } from 'react';
import { Bell, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const INTERVAL_OPTIONS = [
  { value: 0, label: 'リアルタイム（毎回通知）' },
  { value: 5, label: '5分' },
  { value: 30, label: '30分' },
  { value: 60, label: '1時間' },
  { value: 360, label: '6時間' },
  { value: 720, label: '12時間' },
  { value: 1440, label: '24時間' },
];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState({
    notify_on_like: true,
    notify_on_comment: true,
    notify_on_reply: true,
    notify_on_participate: true,
    notify_interval_minutes: 5,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled || !user?.email) return;
        setUserEmail(user.email);

        const { data, error } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_email', user.email)
          .maybeSingle();

        if (cancelled) return;
        if (error) throw error;

        if (data) {
          setPrefs({
            notify_on_like: data.notify_on_like,
            notify_on_comment: data.notify_on_comment,
            notify_on_reply: data.notify_on_reply ?? true,
            notify_on_participate: data.notify_on_participate ?? true,
            notify_interval_minutes: data.notify_interval_minutes ?? 5,
          });
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!userEmail) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_email: userEmail,
          notify_on_like: prefs.notify_on_like,
          notify_on_comment: prefs.notify_on_comment,
          notify_on_reply: prefs.notify_on_reply,
          notify_on_participate: prefs.notify_on_participate,
          notify_interval_minutes: prefs.notify_interval_minutes,
        }, { onConflict: 'user_email' });

      if (error) throw error;
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">通知設定</h1>
        <p className="text-muted-foreground mt-1">メール通知の受信設定を管理します。</p>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <p className="text-sm text-green-600">設定を保存しました。</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            メール通知
          </CardTitle>
          <CardDescription>
            あなたの研究発表に対するアクションがあった際にメールで通知します。
            通知先: {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="notify-like"
                checked={prefs.notify_on_like}
                onCheckedChange={(checked) => setPrefs({ ...prefs, notify_on_like: !!checked })}
              />
              <Label htmlFor="notify-like" className="cursor-pointer">
                <span className="font-medium">いいね通知</span>
                <p className="text-sm text-muted-foreground">誰かがあなたの発表に「いいね」した時に通知</p>
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="notify-comment"
                checked={prefs.notify_on_comment}
                onCheckedChange={(checked) => setPrefs({ ...prefs, notify_on_comment: !!checked })}
              />
              <Label htmlFor="notify-comment" className="cursor-pointer">
                <span className="font-medium">コメント通知</span>
                <p className="text-sm text-muted-foreground">あなたの発表にコメントが投稿された時に通知</p>
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="notify-reply"
                checked={prefs.notify_on_reply}
                onCheckedChange={(checked) => setPrefs({ ...prefs, notify_on_reply: !!checked })}
              />
              <Label htmlFor="notify-reply" className="cursor-pointer">
                <span className="font-medium">返信通知</span>
                <p className="text-sm text-muted-foreground">あなたのコメントに発表者から返信があった時に通知</p>
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="notify-participate"
                checked={prefs.notify_on_participate}
                onCheckedChange={(checked) => setPrefs({ ...prefs, notify_on_participate: !!checked })}
              />
              <Label htmlFor="notify-participate" className="cursor-pointer">
                <span className="font-medium">参加希望通知</span>
                <p className="text-sm text-muted-foreground">あなたの発表に「参加希望」のチェックが入った時に通知</p>
              </Label>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              <span className="font-medium">通知間隔</span>
            </Label>
            <p className="text-sm text-muted-foreground">同じ人から連続でコメントがあった場合、この間隔内では最初の1通だけ通知します。</p>
            <select
              className="w-full sm:w-auto border rounded-md h-10 px-3 bg-background text-sm"
              value={prefs.notify_interval_minutes}
              onChange={(e) => setPrefs({ ...prefs, notify_interval_minutes: parseInt(e.target.value) })}
            >
              {INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <Button onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '設定を保存'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
